import BaseButton from 'app/components/common/BaseButton';
import MarginGroup from 'app/components/layout/MarginGroup';
import { useI18n } from 'app/components/localization';
import BaseModal, { ModalPriority } from 'app/components/modals/Modal';
import { FontWeight } from 'app/components/presentation/text/styles';
import X from 'app/components/svgs/X';
import { COLOR_PALETTE } from 'app/constants/ColorPalette';
import { GREY_B, TRANSPARENT_GREY_A } from 'app/constants/Colors';
import { buttonReset } from 'app/constants/Styles';
import { useCss } from 'app/utils/css';
import React, { FunctionComponent } from 'react';

export interface IModalProps {
  buttons?: React.ReactNode;
  children: React.ReactNode;
  image?: React.ReactNode;
  onClose?: () => void;
  title?: string;
  priority?: ModalPriority;
}

const Modal: FunctionComponent<IModalProps> = ({
  buttons,
  children,
  image,
  onClose,
  title,
  priority,
}) => {
  const css = useCss();
  const { t } = useI18n();

  return (
    <BaseModal onClose={onClose} noBackground shadeColor={TRANSPARENT_GREY_A} priority={priority}>
      <div
        className={css('container', {
          backgroundColor: 'white',
          borderRadius: '4px',
          boxShadow: '0px 2px 4px #00000033',
          boxSizing: 'border-box',
          maxWidth: '525px',
          padding: image !== undefined ? '42px 32px 32px 32px' : '32px',
          position: 'relative',
          textAlign: 'center',
        })}
      >
        {onClose && (
          <BaseButton
            type="button"
            aria-label={t('modal.close')}
            className={css('closeButton', {
              ...buttonReset,
              boxSizing: 'content-box',
              display: 'inline-flex',
              height: '12px',
              position: 'absolute',
              right: '16px',
              top: '16px',
              width: '12px',
            })}
            onClick={onClose}
          >
            <X color={GREY_B} />
          </BaseButton>
        )}

        <MarginGroup>
          {image && (
            <div
              className={css('imageWrapper', {
                display: 'flex',
                justifyContent: 'center',
                marginBottom: '25px',
              })}
            >
              <div className={css('image', { width: '140px' })}>{image}</div>
            </div>
          )}

          <MarginGroup margin="25px">
            {title && <h2
              className={css('title', {
                color: COLOR_PALETTE.grey.darker,
                fontSize: '24px',
                fontWeight: FontWeight.Normal,
                margin: '0',
                textAlign: 'center',
              })}
            >
              {title}
            </h2>}

            <div
              className={css('contents', {
                color: COLOR_PALETTE.grey.dark,
                fontSize: '14px',
                fontWeight: FontWeight.Normal,
                textAlign: 'center',
              })}
            >
              {children}
            </div>

            {buttons && (
              <div
                className={css('buttons', {
                  fontSize: '14px',
                  fontWeight: FontWeight.SemiBold,
                })}
              >
                {buttons}
              </div>
            )}
          </MarginGroup>
        </MarginGroup>
      </div>
    </BaseModal>
  );
};

export default Modal;
